import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { Person, Site, Visit } from "../../shared/types";
import { Dialog, Empty, Field, FlagBadges, ResultBadge, StatusBadge } from "../components/ui";
import { post } from "../lib/api";
import { fmtDate, fmtDuration, fmtTime, fromLocalInput, toLocalInput } from "../lib/format";
import { useApi } from "../lib/useApi";
import { Check, LoadState, PageHeader, thisWeek, toApiError, usePeople, useSites } from "./common";
import {
  isPayable,
  isPending,
  isRejected,
  notPayableReason,
  outRejected,
  shownMinutes,
  type VisitTotals,
  visitTotals,
} from "./payable";

export { isPending };

/** Flags from both punches, without repeats. */
export const visitFlags = (v: Visit) => [...new Set([...v.in.flags, ...(v.out?.flags ?? [])])];

/** "08:00–12:30 · 4h 30m", or what's missing. */
function timesText(v: Visit): string {
  if (!v.out) return `${fmtTime(v.in.at)} · no clock-out yet`;
  if (outRejected(v)) return `${fmtTime(v.in.at)} · clock-out rejected`;
  return `${fmtTime(v.in.at)}–${fmtTime(v.out.at)} · ${fmtDuration(v.minutes)}`;
}

/** "Not payable yet" cell: count of visits, plus their recorded time when there is any. */
export function NotPayable({ t }: { t: Pick<VisitTotals, "notPayableVisits" | "notPayableMinutes"> }) {
  if (!t.notPayableVisits) return <>—</>;
  return (
    <>
      {t.notPayableVisits} {t.notPayableVisits === 1 ? "visit" : "visits"}
      {t.notPayableMinutes > 0 && <span className="muted"> · {fmtDuration(t.notPayableMinutes)}</span>}
    </>
  );
}

export const PAYABLE_HINT =
  "Payable counts only finished visits with both clock-in and clock-out accepted, the same as the payroll export. Not payable yet: waiting for approval, a rejected clock-out, still clocked in, or missing a clock-out.";

/** Clickable table row that also works from the keyboard. */
function RowLink({ to, children, muted }: { to: string; children: ReactNode; muted?: boolean }) {
  const navigate = useNavigate();
  return (
    <tr
      className={`clickable${muted ? " muted" : ""}`}
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(to);
      }}
    >
      {children}
    </tr>
  );
}

/** Visits table used by Timesheets and the person page. */
export function VisitTable({ visits, showPerson = true }: { visits: Visit[]; showPerson?: boolean }) {
  if (!visits.length) return <Empty>No visits match.</Empty>;
  return (
    <>
      {/* Phones get a compact list; wider screens get the table. */}
      <ul className="adm-visit-cards">
        {visits.map((v) => (
          <li key={v.id}>
            <Link to={`/admin/visits/${v.id}`} className={isRejected(v) ? "muted" : ""}>
              <div className="row between">
                <strong>{fmtDate(v.in.at)}</strong>
                <span className="mono">{timesText(v)}</span>
              </div>
              <div>
                {showPerson && <>{v.userName} · </>}
                {v.siteName}
                {v.breakMinutes > 0 && <span className="muted"> · {v.breakMinutes}m break</span>}
              </div>
              <div className="adm-cell-badges">
                <StatusBadge status={v.status} />
                {isPending(v) && <ResultBadge result="pending" />}
                <FlagBadges flags={visitFlags(v)} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <div className="table-wrap adm-visit-table">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Date</th>
              {showPerson && <th>Person</th>}
              <th>Site</th>
              <th>In</th>
              <th>Out</th>
              <th className="num">Worked</th>
              <th className="num">Break</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((v) => (
              <RowLink key={v.id} to={`/admin/visits/${v.id}`} muted={isRejected(v)}>
                <td className="nowrap">{fmtDate(v.in.at)}</td>
                {showPerson && <td>{v.userName}</td>}
                <td>{v.siteName}</td>
                <td className="nowrap mono">{fmtTime(v.in.at)}</td>
                <td className="nowrap mono">
                  {v.out ? (
                    outRejected(v) ? (
                      <s title="Clock-out rejected">{fmtTime(v.out.at)}</s>
                    ) : (
                      fmtTime(v.out.at)
                    )
                  ) : (
                    "—"
                  )}
                </td>
                <td className="num" title={notPayableReason(v) ?? undefined}>
                  {isPayable(v) ? (
                    fmtDuration(v.minutes)
                  ) : (
                    <span className="muted">{fmtDuration(shownMinutes(v))}</span>
                  )}
                </td>
                <td className="num">{v.breakMinutes ? `${v.breakMinutes}m` : "—"}</td>
                <td>
                  <div className="adm-cell-badges">
                    <StatusBadge status={v.status} />
                    {isPending(v) && <ResultBadge result="pending" />}
                    <FlagBadges flags={visitFlags(v)} />
                  </div>
                </td>
              </RowLink>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PersonTotals({ visits }: { visits: Visit[] }) {
  const rows = useMemo(() => {
    const by = new Map<string, Visit[]>();
    for (const v of visits) {
      const list = by.get(v.userId);
      if (list) list.push(v);
      else by.set(v.userId, [v]);
    }
    return [...by.values()]
      .map((list) => ({
        id: list[0]!.userId,
        name: list[0]!.userName,
        flagged: list.filter((v) => !isRejected(v) && visitFlags(v).length > 0).length,
        ...visitTotals(list),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visits]);
  if (!rows.length) return null;
  const sum = (
    k: "payableVisits" | "payableMinutes" | "notPayableVisits" | "notPayableMinutes" | "flagged",
  ) => rows.reduce((s, r) => s + r[k], 0);
  return (
    <>
      <div className="table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Person</th>
              <th className="num">Visits</th>
              <th className="num">Flagged</th>
              <th className="num">Payable</th>
              <th className="num">Not payable yet</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td className="num">{r.payableVisits + r.notPayableVisits}</td>
                <td className="num">{r.flagged || "—"}</td>
                <td className="num">{fmtDuration(r.payableMinutes)}</td>
                <td className="num">
                  <NotPayable t={r} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="num">{sum("payableVisits") + sum("notPayableVisits")}</td>
              <td className="num">{sum("flagged") || "—"}</td>
              <td className="num">{fmtDuration(sum("payableMinutes"))}</td>
              <td className="num">
                <NotPayable
                  t={{
                    notPayableVisits: sum("notPayableVisits"),
                    notPayableMinutes: sum("notPayableMinutes"),
                  }}
                />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="muted small">{PAYABLE_HINT}</p>
    </>
  );
}

function AddVisitDialog({
  people,
  sites,
  onClose,
  onSaved,
}: {
  people: Person[];
  sites: Site[];
  onClose: () => void;
  onSaved: (v: Visit) => void;
}) {
  const nowInput = toLocalInput(Date.now());
  const [userId, setUserId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [inAt, setInAt] = useState("");
  const [outAt, setOutAt] = useState("");
  const [breakMinutes, setBreak] = useState("0");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!userId || !siteId || !inAt) return setError("Choose a person, a site and a clock-in time.");
    if (!reason.trim()) return setError("Please give a reason for adding this visit.");
    const inMs = fromLocalInput(inAt);
    const outMs = outAt ? fromLocalInput(outAt) : null;
    if (outMs != null && outMs <= inMs) return setError("Clock-out must be after clock-in.");
    setBusy(true);
    try {
      const v = await post<Visit>("/admin/visits", {
        userId,
        siteId,
        inAt: inMs,
        outAt: outMs,
        breakMinutes: Number(breakMinutes) || 0,
        reason: reason.trim(),
      });
      onSaved(v);
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a visit" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="muted small">
          For when someone worked but couldn't clock in, e.g. their phone was dead. All times are UK time.
        </p>
        <Field label="Person">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
            <option value="">Choose…</option>
            {people
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Site">
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
            <option value="">Choose…</option>
            {sites
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
        <div className="grid cols-2">
          <Field label="Clock-in">
            <input
              type="datetime-local"
              value={inAt}
              max={nowInput}
              onChange={(e) => setInAt(e.target.value)}
              required
            />
          </Field>
          <Field label="Clock-out" hint="Leave empty if they're still on site.">
            <input
              type="datetime-local"
              value={outAt}
              min={inAt || undefined}
              max={nowInput}
              onChange={(e) => setOutAt(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Break (minutes)">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={600}
            value={breakMinutes}
            onChange={(e) => setBreak(e.target.value)}
          />
        </Field>
        <Field label="Reason" hint="Kept in the audit log.">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. Phone battery died, confirmed with client"
            required
          />
        </Field>
        {error && <div className="banner danger">{error}</div>}
        <div className="row">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? "Saving…" : "Add visit"}
          </button>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function TimesheetsPage() {
  const navigate = useNavigate();
  const week = thisWeek();
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || week.monday;
  const to = params.get("to") || week.today;
  const userId = params.get("userId") ?? "";
  const siteId = params.get("siteId") ?? "";
  const flagged = params.get("flagged") === "1";
  const pending = params.get("pending") === "1";

  const setFilter = (key: string, value: string | boolean) => {
    const next = new URLSearchParams(params);
    const v = typeof value === "boolean" ? (value ? "1" : "") : value;
    if (v) next.set(key, v);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const q = new URLSearchParams({ from, to });
  if (userId) q.set("userId", userId);
  if (siteId) q.set("siteId", siteId);
  if (flagged) q.set("flagged", "1");
  if (pending) q.set("pending", "1");
  const visits = useApi<Visit[]>(from <= to ? `/admin/visits?${q}` : null);
  const people = usePeople();
  const sites = useSites();
  const [adding, setAdding] = useState(false);

  return (
    <div className="page">
      <PageHeader
        title="Timesheets"
        actions={
          <button
            className="btn primary"
            onClick={() => setAdding(true)}
            disabled={!people.data || !sites.data}
          >
            Add visit
          </button>
        }
      />

      <div className="card flat adm-filters">
        <Field label="From">
          <input type="date" value={from} max={to} onChange={(e) => setFilter("from", e.target.value)} />
        </Field>
        <Field label="To">
          <input type="date" value={to} min={from} onChange={(e) => setFilter("to", e.target.value)} />
        </Field>
        <Field label="Person">
          <select value={userId} onChange={(e) => setFilter("userId", e.target.value)}>
            <option value="">Everyone</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Site">
          <select value={siteId} onChange={(e) => setFilter("siteId", e.target.value)}>
            <option value="">All sites</option>
            {(sites.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </Field>
        <div className="checks">
          <Check label="Flagged only" checked={flagged} onChange={(v) => setFilter("flagged", v)} />
          <Check label="Waiting for approval" checked={pending} onChange={(v) => setFilter("pending", v)} />
        </div>
      </div>
      {from > to && <div className="banner warn">The “From” date is after the “To” date.</div>}

      <LoadState state={visits}>
        {(list) => (
          <>
            <section className="adm-section">
              <h2>Totals by person</h2>
              {list.length ? (
                <PersonTotals visits={list} />
              ) : (
                <Empty>No visits in this period with these filters.</Empty>
              )}
            </section>
            <section className="adm-section">
              <h2>
                Visits <span className="badge">{list.length}</span>
              </h2>
              <VisitTable visits={list} />
              {list.length >= 2000 && (
                <p className="muted small">Showing the first 2,000 visits. Narrow the dates to see more.</p>
              )}
            </section>
          </>
        )}
      </LoadState>

      {adding && people.data && sites.data && (
        <AddVisitDialog
          people={people.data}
          sites={sites.data}
          onClose={() => setAdding(false)}
          onSaved={(v) => navigate(`/admin/visits/${v.id}`)}
        />
      )}
    </div>
  );
}
